import React, { FC, memo, SVGProps, useCallback } from 'react';

import { MwdDeeltraject } from '@/apps/mwd-v2/types/Domain';
import { getDeeltrajectRx } from '@/apps/mwd-v2/utils/deeltraject';
import ArrowDownEnd from '@/assets/icons/ArrowDownEnd';
import ArrowDownStart from '@/assets/icons/ArrowDownStart';
import ArrowUp from '@/assets/icons/ArrowUp';
import useClientState from '@/state/useClientState';
import { Group } from '@visx/group';

import styles from '@/apps/mwd-v2/components/Domain/MwdChart.module.css';

type InzetProps = SVGProps<SVGRectElement> & {
  inzet: MwdDeeltraject;
  isSelected: boolean;
  voorgaandeInzetIsBinnen15Min?: boolean;
  isAllowedToMutate: boolean;
  labelX?: number;
};

const Inzet: FC<InzetProps> = ({ inzet, isSelected, isAllowedToMutate, ...rectProps }) => {
  const positionY = inzet.inzetIsKort ? inzet.trapHoogteIndex * 22 : inzet.y;
  const setShowDialog = useClientState((state) => state.setShowDialog);

  /**
   * This handles some of the keyboard shortcuts to execute actions on a Inzet.
   * This is by design, because the inline definition might be cleaner but get
   * redefined every iteration of a Inzet (which will be the most used in a MwdChart).
   * The heavier defined actions are placed in useGeneralShortkeys so that they are calculated just once.
   *
   * @param event
   * @returns
   */
  const handleOnKeydownInzet = useCallback(
    (event: React.KeyboardEvent) => {
      event.preventDefault();

      if ((event.altKey && ['KeyV', 'KeyK'].includes(event.code)) || event.code === 'Delete') {
        event.stopPropagation();
      } else return;

      if (isAllowedToMutate && event.code === 'KeyV') void setShowDialog('inzet-versterken', 'sneltoets');
      if (isAllowedToMutate && event.code === 'KeyK') void setShowDialog('knippen', 'sneltoets');
      // With or without Alt (conform Manual page 21)
      if (isAllowedToMutate && event.code === 'Delete') void setShowDialog('inzet-verzwakken', 'sneltoets');
    },
    [isAllowedToMutate],
  );

  return (
    <Group>
      {isSelected && (
        <rect
          data-testid={`selected-inzet-rect-${inzet.wleId}`}
          data-status={inzet.variant}
          tabIndex={0}
          fill='transparent'
          stroke='var(--tractie-color-border-selected-default)'
          strokeWidth={2}
          width={inzet.width + 4}
          x={-2}
          rx={getDeeltrajectRx(inzet.width + 4, 10)}
          y={positionY - 2}
          height={26}
        />
      )}

      <rect
        data-testid={`inzet-rect-${inzet.wleId}`}
        data-status={inzet.variant}
        tabIndex={0}
        id={`wle-${inzet.wleId}`}
        className={[
          styles.werklijnElementenStyleBase,
          styles[`werklijnElementenStyle${inzet.variant.charAt(0).toUpperCase() + inzet.variant.slice(1)}`],
          inzet.versterkt && styles.versterkt,
          !!inzet.claimsoort && styles.isClaimed,
          isSelected && 'is-selected-v2',
        ].join(' ')}
        width={inzet.width}
        rx={getDeeltrajectRx(inzet.width, 9)}
        y={positionY}
        height={22}
        onKeyDown={handleOnKeydownInzet}
        {...rectProps}
      />

      {inzet.ketenConfig.startKetenBreak && (
        <ArrowDownStart y={positionY + 12} x={inzet.width - 4} aria-hidden='true' data-testid='ketenconflict-icon' />
      )}
      {inzet.ketenConfig.turnKetenBreak && (
        <ArrowDownEnd y={positionY - 10} x={-20} aria-hidden='true' data-testid='ketenconflict-icon' />
      )}
      {inzet.ketenConfig.stopKetenBreak && <ArrowUp y={positionY + 5} x={-30} aria-hidden='true' />}
    </Group>
  );
};

export default memo(Inzet);
